import dayjs from 'dayjs'
import type { FilterToolbarOption } from '@/components/common/FilterToolbar'
import type { MoreFilters } from '@/components/Notifications/MoreFiltersDialog'
import type { AlertType } from '@/services/notificationService'
import { severityParamFromBucket } from '@/utils/severity'
import { toApiPage } from '@/utils/serverPagination'

/** Severity buckets the toolbar offers; 'all' sends no severity filter. */
export type AlertsListSeverity = 'all' | 'low' | 'medium' | 'high'

/**
 * Every type. A MUI Select renders nothing for an empty value, so the
 * "no filter" choice needs a value of its own to stay visible.
 */
export const ALERTS_LIST_ANY_TYPE = 'all'

export type AlertsListFilters = {
  fromDate: Date | null
  toDate: Date | null
  /** A Gateway alertTypeId as a string, or ALERTS_LIST_ANY_TYPE. */
  type: string
  status: AlertsListSeverity
  /** Closed alerts too. Off by default: the home page counts only open alerts. */
  includeClosed: boolean
}

export const ALERTS_LIST_MONTHS_BACK = 2

/**
 * The selection the toolbar shows before the user touches it. The first load
 * sends exactly these values, so the grid always matches the visible filters.
 */
export const createDefaultAlertsListFilters = (): AlertsListFilters => ({
  fromDate: dayjs().subtract(ALERTS_LIST_MONTHS_BACK, 'month').toDate(),
  toDate: dayjs().toDate(),
  type: ALERTS_LIST_ANY_TYPE,
  status: 'all',
  includeClosed: false,
})

export const ALERTS_LIST_SEVERITY_OPTIONS: FilterToolbarOption[] = [
  { value: 'all', label: 'הכל', color: 'transparent' },
  { value: 'high', label: 'חומרה גבוהה', color: '#FF3B30' },
  { value: 'medium', label: 'חומרה בינונית', color: '#FFA500' },
  { value: 'low', label: 'חומרה נמוכה', color: '#F9D71C' },
]

export type AlertTypesState = 'loading' | 'failed' | 'ready'

/**
 * The type dropdown carries Gateway alertTypeIds. When the site has no types
 * the single entry says so instead of offering a list nothing can match.
 */
export const buildAlertTypeOptions = (
  alertTypes: AlertType[],
  state: AlertTypesState
): FilterToolbarOption[] => {
  if (state === 'loading')
    return [{ value: ALERTS_LIST_ANY_TYPE, label: 'טוען סוגי התראה…' }]
  if (state === 'failed')
    return [{ value: ALERTS_LIST_ANY_TYPE, label: 'סוגי ההתראה לא נטענו' }]
  if (alertTypes.length === 0)
    return [{ value: ALERTS_LIST_ANY_TYPE, label: 'לא הוגדרו סוגי התראה באתר' }]
  return [
    { value: ALERTS_LIST_ANY_TYPE, label: 'כולם' },
    ...alertTypes.map(type => ({
      value: String(type.alertTypeId),
      label: type.alertTypeName,
    })),
  ]
}

export type AlertsListPaging = {
  /** 0-based, as the grid counts pages. */
  page: number
  pageSize: number
}

export type AlertsListParams = {
  startDate: string
  endDate: string
  page: number
  size: number
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
  includeClosed: boolean
  alertTypeId?: number
  severity?: number
  meterNumber?: number
  consumerName?: string
  waterCard?: string
  alertName?: string
  openedFrom?: string
  openedTo?: string
  closedFrom?: string
  closedTo?: string
}

export const buildAlertsListParams = (
  filters: AlertsListFilters,
  more: MoreFilters,
  paging: AlertsListPaging
): AlertsListParams => ({
  startDate: dayjs(
    filters.fromDate ??
      dayjs().subtract(ALERTS_LIST_MONTHS_BACK, 'month').toDate()
  ).format('YYYY-MM-DD'),
  endDate: dayjs(filters.toDate ?? new Date()).format('YYYY-MM-DD'),
  page: toApiPage(paging.page),
  size: paging.pageSize,
  sortBy: 'createdAt',
  sortOrder: 'DESC',
  includeClosed: filters.includeClosed,
  alertTypeId:
    filters.type && filters.type !== ALERTS_LIST_ANY_TYPE
      ? Number(filters.type)
      : undefined,
  severity: severityParamFromBucket(filters.status),
  meterNumber: more.meterNumber ? Number(more.meterNumber) : undefined,
  consumerName: more.consumerName || undefined,
  waterCard: more.waterCard || undefined,
  alertName: more.alertName || undefined,
  openedFrom: more.openedFrom?.format('YYYY-MM-DD'),
  openedTo: more.openedTo?.format('YYYY-MM-DD'),
  closedFrom: more.closedFrom?.format('YYYY-MM-DD'),
  closedTo: more.closedTo?.format('YYYY-MM-DD'),
})

export type AlertsListRow = {
  id: number
  alertId: number
  premiseId: number | null
  meterNumber: string | number | null
  consumerName: string | null
  waterCard: string | number | null
}

type WireAlert = {
  alertId?: number
  premiseId?: number | null
  meterNumber?: string | number | null
  consumerName?: string | null
  consumer?: string | null
  waterCard?: string | number | null
}

export const mapAlertsListRow = (
  alert: WireAlert,
  index: number
): AlertsListRow => ({
  id: alert.alertId ?? index + 1,
  alertId: alert.alertId ?? index + 1,
  premiseId: alert.premiseId ?? null,
  meterNumber: alert.meterNumber ?? null,
  consumerName: alert.consumerName ?? alert.consumer ?? null,
  waterCard: alert.waterCard ?? null,
})
